import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * StoreKit 2 for the four subscription products (see PurchasesPlugin.swift).
 *
 * This is the raw bridge: it fetches products, buys one, reads what is
 * active, restores, finishes — and delivers whatever StoreKit hands the app
 * out of band. It knows nothing about the server. The flow that fetches the
 * account token, submits a transaction and only then finishes it lives in
 * services/billingService.ts; use that, not this, from product code.
 *
 * On the web this resolves to a proxy whose methods reject; gate on
 * isNativeApp().
 */

/** The four products, as App Store Connect knows them. The order is the
 * order the UI lists them in; getProducts keeps it. */
export const APPLE_PRODUCT_IDS = {
  memberMonthly: 'com.hialeph.app.member.monthly',
  memberAnnual: 'com.hialeph.app.member.annual',
  scholarMonthly: 'com.hialeph.app.scholar.monthly',
  scholarAnnual: 'com.hialeph.app.scholar.annual',
} as const;

export type AppleProductId = (typeof APPLE_PRODUCT_IDS)[keyof typeof APPLE_PRODUCT_IDS];

export const ALL_APPLE_PRODUCT_IDS: readonly AppleProductId[] = [
  APPLE_PRODUCT_IDS.memberMonthly,
  APPLE_PRODUCT_IDS.memberAnnual,
  APPLE_PRODUCT_IDS.scholarMonthly,
  APPLE_PRODUCT_IDS.scholarAnnual,
];

export interface StoreProduct {
  id: string;
  displayName: string;
  description: string;
  /**
   * Apple's localised price string in the storefront's currency. This is
   * what the UI shows — never a number from /api/pricing, which knows only
   * three currencies and cannot match what Apple will actually charge.
   */
  displayPrice: string;
  price: number;
  /** ISO 4217. Absent below iOS 16. */
  currencyCode?: string;
  subscriptionGroupId?: string;
  period?: { unit: 'day' | 'week' | 'month' | 'year' | 'unknown'; value: number };
}

export interface StoreTransaction {
  /** Changes on every renewal. The replay key for the server. */
  id: string;
  /** Stable for the life of the subscription. */
  originalId: string;
  productId: string;
  /** Epoch milliseconds. */
  purchaseDate: number;
  expirationDate?: number;
  revocationDate?: number;
  /** The UUID attached at purchase, lowercased. Absent if none was. */
  appAccountToken?: string;
  /** 'Sandbox' | 'Production' | 'Xcode'. Absent below iOS 16. */
  environment?: string;
  /**
   * The signed representation the server verifies. Everything else in this
   * object is for display and bookkeeping on the device.
   */
  jws: string;
  /** StoreKit's own verification. Reported, but not the security boundary. */
  verified: boolean;
  /**
   * From the subscription's renewal info, on current entitlements only:
   * whether it will renew, and as which product. A downgrade or a change of
   * period is a preference for the next renewal, so `autoRenewProductId`
   * differing from `productId` means "this, until expirationDate, then that".
   */
  willAutoRenew?: boolean;
  autoRenewProductId?: string;
}

export type PurchaseOutcome =
  | { state: 'purchased'; transaction: StoreTransaction }
  | { state: 'cancelled' }
  /** Ask to Buy, or a payment awaiting action; a transaction may follow
   * later through the transactionUpdated listener. */
  | { state: 'pending' };

export interface PurchasesPlugin {
  getProducts(options: { ids: readonly string[] }): Promise<{ products: StoreProduct[] }>;
  /**
   * `appAccountToken` must be a UUID; the plugin refuses anything else,
   * because Apple would accept the purchase and silently drop the token.
   * The transaction is NOT finished by this call.
   */
  purchase(options: { productId: string; appAccountToken: string }): Promise<PurchaseOutcome>;
  getCurrentEntitlements(): Promise<{ transactions: StoreTransaction[] }>;
  /** Restore Purchases: AppStore.sync(), then the current entitlements. */
  restore(): Promise<{ transactions: StoreTransaction[] }>;
  /** Only once the server has recorded the transaction. */
  finishTransaction(options: { id: string }): Promise<{ finished: boolean }>;
  /**
   * The system's subscription management sheet, in-app, where the customer
   * can change or cancel. Resolves once it is dismissed. Nothing on our side
   * can cancel a subscription; this is the way to the one thing that can.
   */
  showManageSubscriptions(): Promise<void>;
  /**
   * Anything StoreKit delivers out of band: a renewal, a refund, an Ask to
   * Buy approval, a purchase that completed while the app was killed. The
   * native listener runs from launch and retains events until this is
   * attached, so the first event may be waiting.
   */
  addListener(
    eventName: 'transactionUpdated',
    listener: (transaction: StoreTransaction) => void,
  ): Promise<PluginListenerHandle>;
}

export const Purchases = registerPlugin<PurchasesPlugin>('Purchases');
